// liveData.ts (GH3) - the browser-side D1 data loader. The engine's own data loaders (strategy/testdata.ts,
// the S17 harness) read the shipped corpus with node:fs, which cannot run in the browser. Here we load the
// SAME committed JSON - archetypes, the exploit-rule table, and the preflop range sets - through Vite's
// import.meta.glob, so the live advisor (liveRunner.ts) reasons against the real shipped data, not a copy.
//
// Single source of truth: the glob reaches straight into packages/engine/src/data/** (the exact files
// testdata.ts reads), so there is zero drift - no data is duplicated into the web app. Every module is
// parsed through the S2 zod validators, so a malformed corpus fails loudly at load, same as on the engine
// side. Vite inlines the JSON into the bundle at build time (a few hundred KB raw, far less gzipped) and
// the whole thing plays offline at $0.
import {
  ArchetypeSchema,
  ExploitRuleTableSchema,
  RangeSetSchema,
  type Archetype,
  type ArchetypeId,
  type ExploitRule,
  type RangeSet,
} from '@postflop/schema';

// Eager globs: each value is the parsed JSON (import: 'default'). Paths are relative to THIS file
// (apps/web/src/engine) reaching up to the repo root, then into the engine's shipped data dir.
const archetypeModules = import.meta.glob('../../../../packages/engine/src/data/archetypes/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, unknown>;
const rangeModules = import.meta.glob('../../../../packages/engine/src/data/ranges/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, unknown>;
const ruleModules = import.meta.glob('../../../../packages/engine/src/data/rules/rules.v1.json', {
  eager: true,
  import: 'default',
}) as Record<string, unknown>;

/** All shipped archetypes, keyed by id, each validated against ArchetypeSchema. */
export const ARCHETYPES: Map<ArchetypeId, Archetype> = (() => {
  const map = new Map<ArchetypeId, Archetype>();
  for (const raw of Object.values(archetypeModules)) {
    const a = ArchetypeSchema.parse(raw);
    map.set(a.id, a);
  }
  return map;
})();

/** All shipped preflop range sets, keyed by their `.id` (e.g. 'live_loose_9max'), validated. */
export const RANGE_SETS: Map<string, RangeSet> = (() => {
  const map = new Map<string, RangeSet>();
  for (const raw of Object.values(rangeModules)) {
    const rs = RangeSetSchema.parse(raw);
    map.set(rs.id, rs);
  }
  return map;
})();

/** The shipped exploit-rule table (the same rules.v1.json the strategy resolver is tested against). */
export const RULES: ExploitRule[] = (() => {
  const raw = Object.values(ruleModules)[0];
  if (raw === undefined) throw new Error('liveData: rules.v1.json not found via glob');
  return ExploitRuleTableSchema.parse(raw).rules;
})();

/** One archetype by id (throws if absent - a corpus/logic bug, never a user path). */
export const archetypeOf = (id: ArchetypeId): Archetype => {
  const a = ARCHETYPES.get(id);
  if (!a) throw new Error(`liveData: archetype '${id}' not found in corpus`);
  return a;
};

/** One range set by id (throws if absent). */
export const rangeSetOf = (id: string): RangeSet => {
  const rs = RANGE_SETS.get(id);
  if (!rs) throw new Error(`liveData: range set '${id}' not found in corpus`);
  return rs;
};
